<template>
  <listing-layout>
    <h1 slot="heading" class="h2 mb-3">{{ config.title }}</h1>

    <div slot="content">
      <div class="alert alert-success" v-if="successMessage">{{ successMessage }}</div>
      <div class="alert alert-warning" v-if="errorMessage">{{ errorMessage }}</div>

      <!-- Zone / Status / BoY Status dropdown filters (follow-up on top of the legacy page,
           same pattern as Horses) — not part of the original legacy Members page. -->
      <filter-bar :active="hasActiveFilters" @clear="clearAll">
        <filter-multiselect
          label="Zone"
          option-label="code"
          placeholder="Any zone"
          :value="filters.zone_id"
          :options="zoneOptions"
          @input="ids => setFilter('zone_id', ids)"
          @clear="clearFilter('zone_id')"
        />
        <filter-multiselect
          label="Status"
          placeholder="Any status"
          :value="filters.status_id"
          :options="riderStatusOptions"
          @input="ids => setFilter('status_id', ids)"
          @clear="clearFilter('status_id')"
        />
        <filter-multiselect
          label="BoY Status"
          placeholder="Any BoY status"
          :value="filters.boy_status_id"
          :options="riderStatusOptions"
          @input="ids => setFilter('boy_status_id', ids)"
          @clear="clearFilter('boy_status_id')"
        />
        <filter-checkbox
          label="Primary Only"
          :value="filters.primary_only"
          @input="v => setFilter('primary_only', v)"
        />
        <filter-checkbox
          v-if="isAdmin"
          label="Member Secretaries"
          :value="filters.secretary_members_only"
          @input="v => setFilter('secretary_members_only', v)"
        />
      </filter-bar>

      <!-- Same row/level as the legacy DataTable.vue: filter tabs on the left (one row per
           `options.filter` group — financial, then membership_status), Search on the right. -->
      <div class="form-inline mb-sm-3 d-flex justify-content-between">
        <div>
          <div class="d-flex col-12 col-md-auto px-0 mb-3 mb-md-2">
            <label for="showFinancial-all" class="sr-only">Financial</label>
            <div class="filter flex-wrap">
              <div class="form-check pl-0" v-for="tab in FINANCIAL_TABS" :key="tab.value">
                <input
                  class="form-check-input"
                  type="radio"
                  name="showFinancial"
                  :id="'showFinancial-' + tab.value"
                  :value="tab.value"
                  :checked="filters.financial === tab.value"
                  @change="setFilter('financial', tab.value)"
                >
                <label class="form-check-label" :for="'showFinancial-' + tab.value">{{ tab.label }}</label>
              </div>
            </div>
          </div>
          <div class="d-flex col-12 col-md-auto px-0 mb-3 mb-md-2">
            <label for="showMembershipStatus-all" class="sr-only">Membership Status</label>
            <div class="filter flex-wrap">
              <div class="form-check pl-0" v-for="tab in MEMBERSHIP_STATUS_TABS" :key="tab.value">
                <input
                  class="form-check-input"
                  type="radio"
                  name="showMembershipStatus"
                  :id="'showMembershipStatus-' + tab.value"
                  :value="tab.value"
                  :checked="filters.membership_status === tab.value"
                  @change="setFilter('membership_status', tab.value)"
                >
                <label class="form-check-label" :for="'showMembershipStatus-' + tab.value">{{ tab.label }}</label>
              </div>
            </div>
          </div>
        </div>

        <div class="form-group col-12 col-lg-auto mt-2 mt-lg-0 mt-xl-0 ml-auto px-0 action_section">
          <label for="membersSearch" class="d-none d-lg-inline mr-xl-2 mb-0 pr-2 font-weight-normal">Search</label>
          <div class="input-append d-flex flex-grow-1 flex-md-grow-0 mr-md-2">
            <input
              id="membersSearch"
              v-model="searchInput"
              type="text"
              class="form-control mb-0 flex-grow-1 flex-md-grow-0"
              @keyup.enter="applySearch"
              @search="applySearch"
            >
            <svg
              class="append"
              role="button"
              aria-label="Search"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 14 14"
              width="16"
              height="16"
              @click="applySearch"
            >
              <rect width="100%" height="100%" fill="none" />
              <g>
                <path fill="#231815" d="M5.5 11C2.5 11 0 8.5 0 5.5S2.5 0 5.5 0 11 2.5 11 5.5 8.5 11 5.5 11zm0-10C3 1 1 3 1 5.5S3 10 5.5 10 10 8 10 5.5 8 1 5.5 1z" />
                <path fill="#231815" d="M9.5 8.8l4.4 4.4c.2.2.2.5 0 .7l-.4.1-.4-.1-4.4-4.4.8-.7z" />
              </g>
            </svg>
          </div>
        </div>
      </div>

      <div class="position-relative" style="min-height: 240px">
        <responsive-table
          :columns="columns"
          :rows="rows"
          :loading="loading"
          :breakpoints="breakpoints"
          :sort="filters.sort"
          :direction="filters.direction"
          empty-text="No members found."
          @sort="setSort"
        >
          <template #cell:name="{ row }">
            <list-name-cell
              :label="`${row.first_name} ${row.last_name}${row.is_event_membership ? ' (CM)' : ''}`"
              :to="{ name: 'Member Profile', params: { slug: row.id } }"
            >
              <template #badges>
                <span v-if="row.is_primary" class="badge alert-prim">Primary</span>
                <span class="badge" :class="statusBadgeClass(row)">{{ statusBadgeLabel(row) }}</span>
              </template>
            </list-name-cell>
          </template>

          <template #cell:first_name="{ row }">
            <router-link :to="{ name: 'Member Profile', params: { slug: row.id } }">{{ row.first_name }}</router-link>
          </template>
          <template #cell:last_name="{ row }">
            <list-name-cell
              align="right"
              :label="`${row.last_name}${row.is_event_membership ? ' (CM)' : ''}`"
              :to="{ name: 'Member Profile', params: { slug: row.id } }"
            >
              <template #badges>
                <span class="badge" :class="statusBadgeClass(row)">{{ statusBadgeLabel(row) }}</span>
              </template>
            </list-name-cell>
          </template>
          <template #cell:member_number="{ row }">
            <list-name-cell align="right" :label="row.member_number">
              <template #badges>
                <app-tooltip v-if="isAdmin && row.committee_id" :text="`Committee: ${row.committee_name} — click to open this committee`">
                  <router-link
                    :to="{ name: 'Committee Profile', params: { id: String(row.committee_id) } }"
                    class="member-committee-link"
                    :aria-label="`Open ${row.committee_name} committee`"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                      <path fill="currentColor" d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
                    </svg>
                  </router-link>
                </app-tooltip>
                <span v-if="row.is_primary" class="badge alert-prim">Primary</span>
              </template>
            </list-name-cell>
          </template>
          <template #cell:seasons="{ row }">
            <span class="d-flex align-items-center flex-wrap">
              <span v-for="(value, season) in row.seasons" :key="season" class="badge season-badge ml-1" :class="value === 1 ? 'alert-success' : 'alert-danger'">{{ season }}</span>
            </span>
          </template>
          <template #cell:zone_id="{ row }">{{ zoneCode(row.zone_id) }}</template>

          <template #cell:edit="{ row }">
            <router-link class="btn btn-sm btn-primary text-nowrap member-action-btn" :to="{ name: 'Member Profile', params: { slug: row.id } }">Edit</router-link>
          </template>
          <template #cell:login="{ row }">
            <button class="btn btn-sm btn-primary text-nowrap member-action-btn" :disabled="loggingInId === row.id" @click="login(row.parent_id || row.id)">Login</button>
          </template>
          <template #cell:restore="{ row }">
            <button class="btn btn-sm btn-primary text-nowrap member-action-btn" :disabled="restoringId === row.id" @click="askRestore(row)">Restore</button>
          </template>
        </responsive-table>
      </div>

      <!-- Same markup/classes as the legacy DataTable footer (DataTableFields/ShowEntries.vue) -->
      <div class="form-inline justify-content-between align-items-start mt-3">
        <div class="d-flex align-items-center justify-content-center justify-content-md-start form-group mb-3 mb-md-0 col-12 col-md-auto px-0">
          <label for="membersShow" class="mr-2 mb-0 font-weight-normal">Show</label>
          <select
            id="membersShow"
            class="form-control mr-2"
            :value="filters.per_page"
            @change="setPerPage(Number($event.target.value))"
          >
            <option v-for="n in perPageOptions" :key="n" :value="n">{{ n }}</option>
          </select>
          <span>Entries</span>
        </div>

        <div class="pagination-container form-group align-items-start justify-content-center mb-3 mb-md-0" v-show="totalPages > 1">
          <pagination
            :total-pages="totalPages"
            :page="page"
            :on-change="setPage"
            :disabled="loading"
          />
        </div>
      </div>

      <modal v-if="memberToRestore" @close="memberToRestore = null">
        <div slot="body">
          <div class="alert alert-warning text-center">
            You are about to restore {{ memberToRestore.first_name }} {{ memberToRestore.last_name }}. Are you sure?
          </div>
          <div class="d-flex justify-content-center">
            <button class="btn btn-outline-primary" @click="memberToRestore = null">Cancel</button>
            <button class="btn btn-primary ml-2" @click="confirmRestore">Confirm</button>
          </div>
        </div>
      </modal>
    </div>
  </listing-layout>
</template>

<script>
/* Use the "slot" attribute to match the heading and content
 * to their respective fields in the ListingLayout component. */

import { storeSuperSecretaryFlags } from '@/services/actingCommittee'
import axios from 'axios'
import { defineComponent, ref, computed, watch, onMounted, onBeforeUnmount, getCurrentInstance } from '@vue/composition-api'
import listingLayout from '@/components/ListingLayout'
import ResponsiveTable from '@/components/ResponsiveTable'
import Pagination from '@/components/Pagination'
import modal from '@/components/Modal'
import FilterBar from '@/components/filters/FilterBar'
import FilterMultiselect from '@/components/filters/FilterMultiselect'
import FilterCheckbox from '@/components/filters/FilterCheckbox'
import ListNameCell from '@/components/ListNameCell'
import AppTooltip from '@/components/AppTooltip'
import membersApi from '@/services/api/members'
import useMembersList from '@/composables/members/useMembersList'
import useUserRole from '@/composables/useUserRole'
import { buildColumns, buildBreakpoints, FINANCIAL_TABS, MEMBERSHIP_STATUS_TABS } from './columns'

const DEFAULT_CONFIG = { title: 'Members' }

export default defineComponent({
  name: 'Members',
  components: {
    'listing-layout': listingLayout,
    ResponsiveTable,
    Pagination,
    modal,
    FilterBar,
    FilterMultiselect,
    FilterCheckbox,
    ListNameCell,
    AppTooltip
  },
  setup() {
    const { proxy } = getCurrentInstance()
    const store = proxy.$store
    const { isAdmin } = useUserRole()

    // Computed, not a const: the same component instance is reused when navigating between
    // /members and /admin-members (same as Horses/Drafts — see useServerList.js / table.md).
    const config = computed(() => ({ ...DEFAULT_CONFIG, ...(proxy.$route.meta.memberList || {}) }))

    const {
      rows, loading, page, totalPages, filters, perPageOptions,
      fetch, setPage, setSort, setPerPage, setSearch, setFilter, clearFilter, clearFilters, syncFromQuery
    } = useMembersList()

    const isDeletedTab = computed(() => filters.membership_status === 'Deleted')
    const columns = computed(() => buildColumns(isAdmin, isDeletedTab.value))
    const breakpoints = computed(() => buildBreakpoints(isAdmin, isDeletedTab.value))

    // Zone / Status / BoY Status dropdown filter options — same store lookups Horses uses.
    // Status and BoY Status share the single `riderStatus` lookup (Maiden/Novice/Open) —
    // they just filter two different columns (rider_status_id vs boy_status_id).
    const zoneOptions = computed(() => store.state.listZones || [])
    const riderStatusOptions = computed(() => store.state.riderStatus || [])

    const hasActiveFilters = computed(() =>
      Boolean(filters.search)
      || filters.financial !== 'all'
      || filters.membership_status !== 'all'
      || filters.zone_id.length > 0
      || filters.status_id.length > 0
      || filters.boy_status_id.length > 0
      || Boolean(filters.primary_only)
      || Boolean(filters.secretary_members_only)
    )

    const searchInput = ref(filters.search || '')
    const successMessage = ref('')
    const errorMessage = ref('')

    function zoneCode(id) {
      const zone = (store.state.listZones || []).find(z => z.id === id)
      return zone ? zone.code : 'Unspecified'
    }

    // Mirrors the legacy Text.vue `showActive` badge: membership_status when it's not the
    // default "Active", otherwise fall back to the Financial/Unfinancial flag.
    function statusBadgeLabel(row) {
      if (row.membership_status !== 'Active') return row.membership_status === 'Onhold' ? 'On Hold' : row.membership_status
      return row.financial ? 'Financial' : 'Unfinancial'
    }
    function statusBadgeClass(row) {
      if (row.membership_status !== 'Active') {
        if (row.membership_status === 'Lifetime') return 'alert-info'
        if (row.membership_status === 'Archived') return 'alert-dark'
        return 'alert-danger'
      }
      return row.financial ? 'alert-info' : 'alert-warning'
    }

    // Search-as-you-type with a 500ms debounce; Enter / the icon fire it immediately.
    let searchTimer = null
    function applySearch() {
      clearTimeout(searchTimer)
      if (searchInput.value !== filters.search) setSearch(searchInput.value)
    }
    // "Clear all filters" (FilterBar) — wipes search + tabs + dropdowns back to defaults in one shot.
    function clearAll() {
      clearTimeout(searchTimer)
      searchInput.value = ''
      clearFilters()
    }
    watch(searchInput, (val) => {
      clearTimeout(searchTimer)
      searchTimer = setTimeout(() => {
        if (val !== filters.search) setSearch(val)
      }, 500)
    })
    onBeforeUnmount(() => clearTimeout(searchTimer))

    // --- Login as member (admin only) ---
    const loggingInId = ref(null)
    function login(id) {
      loggingInId.value = id
      axios.post(`${proxy.$api}/auth/login-as-user`, { id }).then((response) => {
        if (response.data.token) {
          localStorage.setItem('bearer', response.data.token)
          axios.defaults.headers.common.Authorization = `Bearer ${response.data.token}`
          localStorage.setItem('first_name', response.data.first_name)
          localStorage.setItem('last_name', response.data.last_name)
          localStorage.setItem('role', response.data.role)
          localStorage.setItem('userID', response.data.id)
          localStorage.setItem('is_onboarded', response.data.is_onboarded)
          storeSuperSecretaryFlags(response.data, { resetSelection: true })
          proxy.$router.push('/')
        }
      }).catch(() => {
        errorMessage.value = 'Unable to log in as this member. Please try again later.'
      }).finally(() => { loggingInId.value = null })
    }

    // --- Restore a soft-deleted member (admin only, "Deleted" tab) ---
    const memberToRestore = ref(null)
    const restoringId = ref(null)
    function askRestore(row) {
      memberToRestore.value = row
    }
    function confirmRestore() {
      const row = memberToRestore.value
      memberToRestore.value = null
      restoringId.value = row.id
      successMessage.value = ''
      errorMessage.value = ''
      membersApi.restore(row.id).then((response) => {
        successMessage.value = (response.data || {}).message || 'The member has been restored.'
        fetch()
      }).catch(() => {
        errorMessage.value = 'Unable to restore this member. Please try again later.'
      }).finally(() => { restoringId.value = null })
    }

    function loadPage() {
      fetch()
    }

    onMounted(loadPage)

    // Re-load when switching between /members <-> /admin-members — the component instance is
    // kept, so onMounted won't fire again (same reuse issue as Horses/Drafts).
    watch(() => proxy.$route.path, () => {
      clearTimeout(searchTimer)
      syncFromQuery()
      searchInput.value = filters.search || ''
      successMessage.value = ''
      errorMessage.value = ''
      loadPage()
    })

    return {
      config,
      isAdmin,
      columns,
      breakpoints,
      rows,
      loading,
      page,
      totalPages,
      filters,
      perPageOptions,
      searchInput,
      successMessage,
      errorMessage,
      FINANCIAL_TABS,
      MEMBERSHIP_STATUS_TABS,
      zoneOptions,
      riderStatusOptions,
      hasActiveFilters,
      setPage,
      setSort,
      setPerPage,
      setFilter,
      clearFilter,
      clearAll,
      applySearch,
      zoneCode,
      statusBadgeLabel,
      statusBadgeClass,
      loggingInId,
      login,
      memberToRestore,
      restoringId,
      askRestore,
      confirmRestore
    }
  }
})
</script>

<style scoped>
/* Bootstrap's .btn-sm is still sized for a longer label — shrink Edit/Login/Restore to match
   the Horses list's Remove button (same treatment, same values: see .remove-btn there). */
.member-action-btn {
  padding: 0.2rem 0.5rem;
  font-size: 0.8rem;
}

@media (max-width: 768px) {
  .member-action-btn {
    padding: 0.15rem 0.35rem;
    font-size: 0.75rem;
  }
}

/* The global `.badge { min-width: 58px }` (_badges.scss) pads short labels — a 4-char season
   like "2026" gets blown out to 58px. Status/Primary badges don't show this because their text
   ("Financial", "On Hold", "Primary"...) is already close to or past that width. */
.season-badge {
  min-width: 0;
  padding-left: 1.1em;
  padding-right: 1.1em;
}

/* Committee icon (Member No. column, admin only) — jumps a secretary's row to their committee.
   Same icon/treatment as the Committee-cell "find secretaries" link on the Drafts list. */
.member-committee-link {
  display: inline-flex;
  flex: none;
  line-height: 0;
  color: var(--gray-600, #6c757d);
}

.member-committee-link:hover {
  color: var(--primary, #aa2d41);
}
</style>



// WEBPACK FOOTER //
// src/views/Members/Listing/Members.vue